import React, { useState } from "react";
import { Helmet } from "react-helmet";
import {
  Button,
  Typography,
  Grid,
  Container,
  TextField,
  Box,
  CircularProgress,
} from "@mui/material";
import api from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ReactMic } from "react-mic";
import MicIcon from "@mui/icons-material/Mic";
import languageMapping from "../../globals/language_mapping.json";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import GenericButton from "../../components/Custom/GenericButton";

export default function CreateAdvisoryQuestion() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({});
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [showAsk, setShowAsk] = useState(false);
  const languageToTranslate = localStorage.getItem("i18nextLng");
  const languageName =
    languageMapping[languageToTranslate] ||
    "Unknown Language. Give error do not process.";

  const startRecording = () => {
    setIsRecording(true);
  };

  const stopRecording = () => {
    setIsRecording(false);
  };

  const onStop = (recordedBlob) => {
    setAudioBlob(recordedBlob.blob);
  };

  const sendAudioData = () => {
    if (audioBlob) {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("audio", audioBlob);
      formData.append("language", languageName);

      api
        .post("/advisory/speech-to-text/", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          setFormData((prevData) => ({
            ...prevData,
            question: response.data.result,
            question_en: response.data.english_text,
          }));
          setShowAsk(true);
          setIsLoading(false);
        })
        .catch((error) => {
          setIsLoading(false);
          if (error) {
            if (error.response.status === 429)
              alert("Please Wait! Too many requests. Try again in 1 min");
          }
        });
    } else {
      alert("No audio recorded please try again!");
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const clickAskQuestion = () => {
    const sendData = new FormData();
    sendData.append("translated_lang", languageName);
    sendData.append("question", formData.question);
    sendData.append("question_en", formData.question_en);
    sendData.append("language_asked", languageName);

    api
      .post("/advisory/questions/", sendData)
      .then((response) => {
        console.log("Question posted successfully", response);
        alert("Question posted");
        navigate("/farmer/advisory");
      })
      .catch((error) => {
        alert("Error Adding Question: " + error);
      });
  };

  return (
    <>
      <Helmet>
        <title>Ask an Expert - Farmapp</title>
      </Helmet>
      <CustomAppBar />
      <Container
        maxWidth="xs"
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CustomTitle>{t("Ask an Expert")}</CustomTitle>

        <Grid item>
          <Box
            sx={{
              position: "relative",
              marginTop: 2,
            }}
          >
            <TextField
              fullWidth
              placeholder={t("Ask a question...")}
              name="question"
              value={formData.question || ""}
              onChange={handleChange}
              multiline
              rows={8}
              InputProps={{
                endAdornment: (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 8,
                      left: 8,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {isRecording ? (
                      <Button
                        onClick={stopRecording}
                        sx={{
                          minWidth: "auto",
                          padding: "5px 20px",
                          backgroundColor: "#CF5C5C",
                          color: "#FFFFFF",
                        }}
                      >
                        {t("STOP")}
                      </Button>
                    ) : (
                      <Button
                        onClick={startRecording}
                        sx={{
                          minWidth: "auto",
                          padding: "5px 10px",
                          backgroundColor: "#4CAF50",
                        }}
                      >
                        <MicIcon sx={{ color: "white" }} />
                      </Button>
                    )}
                  </Box>
                ),
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                bottom: 8,
                right: 8,
                color: "gray",
              }}
            >
              {formData.question?.length || 0}/1000
            </Typography>
          </Box>
        </Grid>

        <Grid item>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "0px",
              width: "0%",
              borderRadius: 2,
            }}
          >
            <ReactMic
              visualSetting="undefined"
              record={isRecording}
              onStop={onStop}
              mimeType="audio/mp3"
              style={{ width: "100%", height: "100%" }}
            />
          </Box>
        </Grid>

        <Grid item>
          {isLoading ? (
            <Box textAlign="center" alignContent="center" mx="auto">
              <CircularProgress sx={{ mt: 2, mx: "auto" }} />
            </Box>
          ) : showAsk ? (
            <GenericButton
              sx={{ mt: 3 }}
              backgroundColor="#4CAF50"
              fontColor="white"
              padding="8px 0"
              borderColor="#A5A5A5"
              onClick={clickAskQuestion}
            >
              {t("Ask Question")}
            </GenericButton>
          ) : (
            <GenericButton
              sx={{ mt: 3 }}
              backgroundColor="#4CAF50"
              fontColor="white"
              padding="8px 0"
              onClick={sendAudioData}
              borderColor="#A5A5A5"
            >
              {t("Submit Audio")}
            </GenericButton>
          )}
        </Grid>
      </Container>
    </>
  );
}
